import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PenLine, Info } from "lucide-react";
import { API } from "../../App";
import { SignaturePad } from "./SignaturePad";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "");

const SignOffDialog = ({ open, onOpenChange, token, dwelling, defaultName, onSigned }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [name, setName] = useState(defaultName || "");
  const [sig, setSig] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/handover-public/${token}/dwellings/${dwelling.id}/sign-off`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmed, client_name: name, signature_data: sig }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not record your sign-off");
      toast.success("Thank you — your sign-off has been recorded");
      onOpenChange(false);
      onSigned();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="client-signoff-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8"><PenLine className="w-5 h-5 text-amber-600" />Final Sign-off — {dwelling.dwelling_number}</DialogTitle>
          <DialogDescription>A copy of the signed record will be emailed to you.</DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-3 rounded-xl bg-green-50 border border-green-200 p-4 cursor-pointer">
          <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" data-testid="signoff-confirm-checkbox" />
          <span className="font-semibold text-slate-900">I confirm all items are complete</span>
        </label>
        <div className="space-y-1.5">
          <Label htmlFor="signoff-name">Your name *</Label>
          <Input id="signoff-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="signoff-name-input" />
        </div>
        <SignaturePad value={sig} onChange={setSig} />
        <Button disabled={saving || !confirmed || !name.trim() || !sig} onClick={submit} className="w-full h-11 bg-green-600 hover:bg-green-700 text-white" data-testid="signoff-submit-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign Off"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const SignOffPanel = ({ token, dwelling, defaultName, onSigned }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border-2 border-green-300 bg-green-50 p-5 space-y-3" data-testid={`signoff-panel-${dwelling.dwelling_number}`}>
      <p className="flex items-center gap-2 font-bold text-green-900"><CheckCircle2 className="w-5 h-5" />All items are complete</p>
      <p className="text-sm text-slate-700">Please review the items below, then sign to confirm everything has been completed.</p>
      <Button onClick={() => setOpen(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`signoff-open-${dwelling.dwelling_number}`}>
        <PenLine className="w-4 h-4 mr-2" />Sign Off
      </Button>
      <SignOffDialog open={open} onOpenChange={setOpen} token={token} dwelling={dwelling} defaultName={defaultName} onSigned={onSigned} />
    </div>
  );
};

export const PreviousSignOffNote = ({ signoff, testId }) => (
  <p className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900" data-testid={testId}>
    <Info className="w-4 h-4 mt-0.5 shrink-0" />
    You signed off on {fmtDate(signoff.signed_at)}. New or reopened items are being attended to — you'll be asked to sign again once they're completed.
  </p>
);

export const SignedThankYou = ({ signoff, dwelling, compact, testId }) => (
  <div className={`rounded-xl bg-white border border-green-200 text-center ${compact ? "p-5" : "p-10"} space-y-2`} data-testid={testId}>
    <CheckCircle2 className={`${compact ? "w-8 h-8" : "w-14 h-14"} text-green-600 mx-auto`} />
    <p className={`${compact ? "text-lg" : "text-2xl"} font-bold text-slate-900`}>Thank you{signoff.signed_name ? `, ${signoff.signed_name}` : ""}</p>
    <p className="text-slate-600">{dwelling ? `${dwelling} was` : "Your handover was"} signed off on {fmtDate(signoff.signed_at)}. A copy of the signed record has been emailed to you.</p>
  </div>
);
